import { getNumber, getString, toInsertLocation } from "./utils";

export function insertTableCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): { ok: true; rowCount: number; columnCount: number } {
  const rowCount = getNumber(args, "rowCount");
  const columnCount = getNumber(args, "columnCount");
  const target = getString(args, "target") as "body" | "selection"; // narrows string → union
  const location = toInsertLocation(args.location);

  let values: string[][] | undefined;
  if (Array.isArray(args.values)) {
    const rows = args.values as unknown[];
    if (rows.length !== rowCount) {
      throw new Error(
        `values has ${rows.length} rows but rowCount is ${rowCount}.`
      );
    }
    values = rows.map((row, ri) => {
      if (!Array.isArray(row)) {
        throw new Error(`values[${ri}] is not an array.`);
      }
      if (row.length !== columnCount) {
        throw new Error(
          `values[${ri}] has ${row.length} columns but columnCount is ${columnCount}.`
        );
      }
      return row.map((cell) => (cell == null ? "" : String(cell)));
    });
  }

  const loc = location as any;
  if (target === "body") {
    ctx.document.body.insertTable(rowCount, columnCount, loc, values);
  } else if (target === "selection") {
    const selection = ctx.document.getSelection();
    selection.insertTable(rowCount, columnCount, loc, values);
  } else {
    throw new Error(`Invalid target "${String(target)}".`);
  }
  return { ok: true, rowCount, columnCount };
}

export async function getTablesCore(
  ctx: Word.RequestContext,
  args: Record<string, unknown> = {}
): Promise<{
  tables: Array<{
    index: number;
    rowCount: number;
    columnCount: number;
    firstRowPreview: string[];
  }>;
  total: number;
  truncated: boolean;
}> {
  const limit = getNumber(args, "limit", false) ?? 50;
  const tables = ctx.document.body.tables;
  tables.load("items/rowCount,items/values");
  await ctx.sync();
  const all = tables.items;
  const slice = all.slice(0, limit);
  return {
    tables: slice.map((t, i) => {
      const values = (t.values ?? []) as string[][];
      const firstRow = values[0] ?? [];
      const columnCount = firstRow.length;
      return {
        index: i,
        rowCount: t.rowCount,
        columnCount,
        firstRowPreview: firstRow.map((c) => String(c ?? "")),
      };
    }),
    total: all.length,
    truncated: all.length > slice.length,
  };
}
